import React from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DollarSign, Target, Sparkles } from "lucide-react";
import { FormData } from './types';

interface SalaryStepProps {
  formData: FormData;
  formErrors: Record<string, string>;
  onInputChange: (field: string, value: string) => void;
  onNext: () => void;
}

export const SalaryStep = ({ formData, formErrors, onInputChange, onNext }: SalaryStepProps) => {
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 mb-6 animate-scale-in">
          <Target className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-3xl font-bold mb-3 gradient-text">First things first</h2>
        <p className="text-text-secondary text-lg leading-relaxed max-w-md mx-auto">
          Let's talk about your income goals and set the foundation for your rate calculation
        </p>
      </div>

      <div className="space-y-6">
        <div className="relative group">
          <label htmlFor="salary" className="block text-base font-medium text-text-primary mb-3 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            How much do you make annually, or would like to make? Be real.
          </label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary font-semibold text-lg">
              $
            </div>
            <Input
              id="salary"
              type="text"
              value={formData.salary}
              onChange={(e) => onInputChange('salary', e.target.value)}
              className="pl-10 pr-4 py-4 text-lg font-medium bg-card-bg border-2 border-border hover:border-primary/50 focus:border-primary transition-all duration-300 rounded-xl"
              placeholder="Enter your annual salary"
            />
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none" />
          </div>
          {formErrors.salary && (
            <p className="text-error mt-2 text-sm flex items-center gap-1 animate-fade-in">
              <span className="w-1 h-1 bg-error rounded-full"></span>
              {formErrors.salary}
            </p>
          )}
        </div>

        <div className="relative group">
          <label htmlFor="bonus" className="block text-base font-medium text-text-primary mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent-cyan" />
            What is the bonus looking like?
          </label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary font-semibold text-lg">
              $
            </div>
            <Input
              id="bonus"
              type="text"
              value={formData.bonus}
              onChange={(e) => onInputChange('bonus', e.target.value)}
              className="pl-10 pr-4 py-4 text-lg font-medium bg-card-bg border-2 border-border hover:border-primary/50 focus:border-primary transition-all duration-300 rounded-xl"
              placeholder="Enter expected bonus"
            />
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none" />
          </div>
          {formErrors.bonus && (
            <p className="text-error mt-2 text-sm flex items-center gap-1 animate-fade-in">
              <span className="w-1 h-1 bg-error rounded-full"></span>
              {formErrors.bonus}
            </p>
          )}
        </div>
      </div>

      <div className="pt-6">
        <Button
          onClick={onNext}
          className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-primary to-secondary hover:from-primary-hover hover:to-secondary-hover transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] rounded-xl"
        >
          Next: Reality Check
          <span className="ml-2">→</span>
        </Button>
      </div>
    </div>
  );
};